import React, { FC, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import evianLogo from '../../../assets/images/evian_red_logo.png';
import { useUIContext } from '../../context/UIContext';
import { API } from '../../utils/API';
import { EVIAN_COLORS } from '../../utils/Colors';
import { EvianZonesMap } from './EvianLeaderBoard';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Page = styled.div`
  background: ${EVIAN_COLORS.leaderboardBg};
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const Container = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${EVIAN_COLORS.panel};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  & > img {
    width: 200px;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${EVIAN_COLORS.text};
  text-align: center;
  margin: 0;
  line-height: 1.2;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${EVIAN_COLORS.text};
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${EVIAN_COLORS.border};
  border-radius: 8px;
  font-size: 1rem;
  color: ${EVIAN_COLORS.text};
  background: ${EVIAN_COLORS.panel};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${EVIAN_COLORS.primary};
    box-shadow: 0 0 0 3px ${EVIAN_COLORS.focusRing};
  }

  &::placeholder {
    color: ${EVIAN_COLORS.muted};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${EVIAN_COLORS.border};
  border-radius: 8px;
  font-size: 1rem;
  color: ${EVIAN_COLORS.text};
  background: ${EVIAN_COLORS.panel};
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${EVIAN_COLORS.primary};
    box-shadow: 0 0 0 3px ${EVIAN_COLORS.focusRing};
  }
`;

const ShowQRButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: ${EVIAN_COLORS.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${EVIAN_COLORS.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 6px 8px -1px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: ${EVIAN_COLORS.muted};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const QRCodeImage = styled.img`
  max-width: 200px;
  width: 100%;
  height: auto;
  border-radius: 8px;
`;


const URLText = styled.div`
  font-size: 0.875rem;
  color: ${EVIAN_COLORS.text};
  text-align: center;
  word-break: break-all;
  padding: 8px 12px;
  font-family: monospace;
`;

export const EvianQRScreen: FC = () => {
  const { toast, loader } = useUIContext();

  const [password, setPassword] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [funZones, setFunZones] = useState<string[]>([]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);
  const handleZoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => setSelectedZone(e.target.value);

  const fetchFunZones = async () => {
    try {
      const response = await API.Evian.getZoneNames();
      setFunZones(response);
    } catch (error: any) {
      toast.showToast(error.message, 'ERROR');
    }
  };

  useEffect(() => {
    fetchFunZones();

  }, []);

  const handleShowQR = async () => {
    loader.showLoader();
    try {
      const response = await API.Evian.getZoneQR(selectedZone, password);
      if (response.valid) {
        setQrCodeUrl(response.qr_url);
        setRegistrationUrl(response.qr_destination_url);
      }
    } catch (err: any) {
      toast.showToast(err.message, 'ERROR');
    } finally {
      loader.hideLoader();
    }
  };

  const isValid = password.length > 0 && selectedZone.length > 0;

  return (
    <Page>
      <Container>
        <LogoContainer>
          <img src={evianLogo} alt="evian" />
        </LogoContainer>

        <Title>Sweepstakes Registration Webpage URL QR Code</Title>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            type="password"
            placeholder="Enter the password"
            value={password}
            onChange={handlePasswordChange}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="funzone">Fan Zone</Label>
          <DropdownContainer>
            <DropdownSelect
              id="funzone"
              value={selectedZone}
              onChange={handleZoneChange}
            >
              <option value="">Select a Fan Zone</option>
              {funZones.map((zone) => (
                <option key={zone} value={zone}>
                  {EvianZonesMap[zone as keyof typeof EvianZonesMap].toUpperCase()}
                </option>
              ))}
            </DropdownSelect>
          </DropdownContainer>
        </FormGroup>

        <ShowQRButton
          onClick={handleShowQR}
          disabled={!isValid}
        >
          Show QR code
        </ShowQRButton>

        {qrCodeUrl && (
          <QRContainer>
            <QRCodeImage src={qrCodeUrl} alt="Register QR Code" />
            <URLText>{registrationUrl}</URLText>
          </QRContainer>
        )}
      </Container>
    </Page>
  );
};
